import { CreditCardStatus, WorkflowPlace } from '@nemuru-eng/js-api-client';

import { AbstractProgressWorkflow } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/ProgressWorkflow/AbstractProgressWorkflow';
import { CREDIT_CARD_STATUS_DEFINITIONS } from '@/components/modules/CreditCard/List/constants/creditCardStatusConfig';
import { CreditCardWorkflow, StatusDefinition } from '@/config/types';

export class CreditCardProgressWorkflow extends AbstractProgressWorkflow {
  private static readonly NON_ACTIVE_STATUSES = [
    CreditCardStatus.DESISTED,
    CreditCardStatus.EXPIRED,
    CreditCardStatus.CANCELLED,
    CreditCardStatus.DENIED,
  ];

  private static readonly DISPLAYED_STEPS = [
    WorkflowPlace.KYC_OCR,
    WorkflowPlace.PERSONAL_INFO,
    WorkflowPlace.ADDITIONAL_INFO,
    WorkflowPlace.ADDRESS,
    WorkflowPlace.PROFESSIONAL_DETAILS,
    WorkflowPlace.BANK_ACCOUNT,
    WorkflowPlace.SUMMARY,
    WorkflowPlace.LOW_LIMIT,
    WorkflowPlace.GDPR,
    WorkflowPlace.INE,
    WorkflowPlace.CONTRACT_SIGNATURE,
    WorkflowPlace.KYC_ADDITIONAL_DOCUMENTATION,
    WorkflowPlace.KYC_VIDEO,
    WorkflowPlace.DOCUMENTATION,
    WorkflowPlace.IBERIA_CLUB,
    WorkflowPlace.COMPLETED,
    WorkflowPlace.DENIED,
  ];

  private static readonly CLIENT_STEPS = [
    WorkflowPlace.INE,
    WorkflowPlace.CONTRACT_SIGNATURE,
    WorkflowPlace.KYC_ADDITIONAL_DOCUMENTATION,
    WorkflowPlace.KYC_VIDEO,
    WorkflowPlace.DOCUMENTATION,
    WorkflowPlace.IBERIA_CLUB,
  ];

  constructor(status: CreditCardStatus, workflow: CreditCardWorkflow) {
    super(status, workflow);
  }

  public get isActive(): boolean {
    return !CreditCardProgressWorkflow.NON_ACTIVE_STATUSES.includes(this.status);
  }

  public get isFinished(): boolean {
    return this.isCompleted || !this.isActive;
  }

  public get steps(): WorkflowPlace[] {
    if (this._workflow.place.name === WorkflowPlace.DENIED) {
      return CreditCardProgressWorkflow.DISPLAYED_STEPS.filter((step) => step !== WorkflowPlace.COMPLETED);
    }

    return CreditCardProgressWorkflow.DISPLAYED_STEPS.filter((step) => step !== WorkflowPlace.DENIED);
  }

  public get statusDefinition(): StatusDefinition {
    return CREDIT_CARD_STATUS_DEFINITIONS[this.status];
  }

  public get shouldClientPerformTheSteps(): boolean {
    const currentPlaceName = this._workflow.place.name;

    if (!currentPlaceName) {
      return false;
    }

    return CreditCardProgressWorkflow.CLIENT_STEPS.includes(currentPlaceName);
  }

  // The credit card leaves the initial status only when the summary is submitted, right before the scoring call, so
  // going back to edit previous steps does not change it. While the workflow is still on the summary, the scoring call
  // has not succeeded yet (the status is set to requested before calling it).
  public get hasPassedScoring(): boolean {
    return this.status !== CreditCardStatus.INITIAL && this._workflow.place.name !== WorkflowPlace.SUMMARY;
  }
}
